import { selectAuthToken, selectCurrentUser, useAuthStore } from '@/store';
import { useMemo } from 'react';

/**
 * Custom hook to get and track the current authenticated user
 * @returns Object containing the current user
 */
export const useCurrentUser = () => {
  const currentUser: any = useAuthStore(selectCurrentUser);
  return currentUser;
};

export const useCurrentUserRole = () => {
  const currentUser: any = useCurrentUser();

  return useMemo(() => {
    // Null-safe (APL-12040): logout nulls the user while consumers (session
    // panes, prompts) are still mounted — all flags read false, no throw.
    const role = currentUser?.role;
    return {
      isQbAuthor: role === 'qb_author',
      isAdmin: role === 'admin',
      isTeacher: role === 'teacher',
      isStudent: role === 'student',
      isParent: role === 'parent',
    };
  }, [currentUser?.role]);
};

export const useCurrentUserOrg = () => {
  const currentUser: any = useCurrentUser();
  return currentUser?.org;
};

/**
 * Custom hook to get and track the current authenticated user's enabled features
 * @returns Object containing the current user's enabled features
 */
export const useEnabledFeatures = () => {
  const currentUserOrg = useCurrentUserOrg();
  if (currentUserOrg) {
    return currentUserOrg?.enabled_features ?? {};
  } else {
    // case where embedded and no currentUser hence no org
    return {};
  }
};

export const useOrgRoles = () => {
  const currentUserOrg = useCurrentUserOrg();
  return currentUserOrg?.roles_and_permissions ?? {};
};

export const useAppRegion = () => {
  // Read from environment variable VITE_REGION
  // 'china' -> Use Supabase
  // 'global' -> Use Firebase (default)
  const region = import.meta.env.VITE_REGION || 'global';
  return region as 'global' | 'china';
};

export const useToken = () => {
  const token = useAuthStore(selectAuthToken);
  return token;
};
